import type { LayoutBox, LayoutContent, LayoutFit, Rect, RenderNote } from "@scenix/core";

import { cssColour, parsableColour } from "./colour";

/**
 * Resolving a layout into the boxes a browser draws (ADR 000066).
 *
 * The same walk the Go renderer does, and deliberately the same shape of
 * answer: both draw the flattened leaves rather than the tree, so a nested box
 * cannot end up in one place in the preview and another in the render.
 */

/** The whole frame, which a root box is measured against. */
export const wholeFrame: Rect = { x: 0, y: 0, w: 1, h: 1 };

/**
 * The shape of the frame, which the renderer encodes at 1280x720.
 *
 * Needed here because a box's shape is what a fit is measured against, and
 * normalised coordinates only say what fraction of the frame a box is. The
 * stage is held to the same ratio in CSS, so the box the browser draws and the
 * box ffmpeg pads into are the same shape.
 */
export const frameAspect = 16 / 9;

/** One leaf, resolved against the whole frame. */
export interface PlacedLeaf {
  rect: Rect;
  content: LayoutContent;
  fit: LayoutFit;
  muted: boolean;
  label: string;
  /** This box's own instructions to the renderer (ADR 000068). */
  notes: readonly RenderNote[];
}

/** Places a rect inside its parent. Nesting is a multiplication. */
export function within(rect: Rect, parent: Rect): Rect {
  return {
    x: parent.x + rect.x * parent.w,
    y: parent.y + rect.y * parent.h,
    w: rect.w * parent.w,
    h: rect.h * parent.h,
  };
}

/** Resolves a layout into leaves placed against the whole frame, in draw order. */
export function flatten(box: LayoutBox, parent: Rect = wholeFrame): PlacedLeaf[] {
  const here = within(box.rect, parent);

  if (box.content) {
    return [
      {
        rect: here,
        content: box.content,
        fit: box.fit ?? "cover",
        muted: box.muted ?? false,
        label: box.label ?? "",
        notes: box.renderNotes ?? [],
      },
    ];
  }
  return (box.boxes ?? []).flatMap((child) => flatten(child, here));
}

/**
 * The CSS that puts a leaf where the layout says.
 *
 * Percentages rather than pixels, so the same numbers work at whatever size the
 * stage happens to be, which is the point of the coordinates being normalised.
 */
export function boxStyle(
  leaf: PlacedLeaf,
  segmentNotes: readonly RenderNote[] = [],
): React.CSSProperties {
  return {
    position: "absolute",
    left: `${leaf.rect.x * 100}%`,
    top: `${leaf.rect.y * 100}%`,
    width: `${leaf.rect.w * 100}%`,
    height: `${leaf.rect.h * 100}%`,
    overflow: "hidden",
    // On the box rather than on the content, because that is what the colour
    // is: ffmpeg pads the box around whatever it fitted into it, so a
    // background note paints exactly the part of the box the content does not
    // reach (ADR 000068). Under cover there is no such part, which is why the
    // renderer reports the note unapplied there and why nothing shows here
    // either.
    ...backgroundStyle(leaf.notes, segmentNotes),
  };
}

/**
 * The CSS for the canvas every box is laid over.
 *
 * The renderer paints it with the segment's background note and falls back to
 * black, so a layout that does not tile the frame shows that colour wherever no
 * box reaches (ADR 000066). Painting it black regardless showed a frame the
 * render would not make, which is the one thing the stage may not do.
 *
 * Only the segment's notes reach here. A box's note is about that box, and the
 * canvas is underneath all of them.
 */
export function stageStyle(segmentNotes: readonly RenderNote[]): React.CSSProperties {
  return backgroundStyle([], segmentNotes);
}

/**
 * The background note as CSS, or nothing at all when neither scope named one.
 *
 * The box and the canvas underneath it ask the same question about different
 * scopes, and both have to spell the answer the way CSS does before a browser
 * sees it. Translated here rather than in each caller, because a caller that
 * forgets paints nothing and reports nothing, which leaves somebody looking at
 * black with no way to tell it from a note that was ignored (issue #260).
 */
function backgroundStyle(
  boxNotes: readonly RenderNote[],
  segmentNotes: readonly RenderNote[],
): React.CSSProperties {
  const background = noteValue("background", boxNotes, segmentNotes);

  return background ? { background: cssColour(background) } : {};
}

/**
 * The CSS that draws a whole-frame picture the way the renderer would.
 *
 * Not a box. A segment that is not composed goes through `frameFilter`, which
 * fits one source into the frame itself, so there is no layout fit underneath
 * to fall through to and the default is the renderer's own: contain unless a
 * note says otherwise (apps/api/internal/render/frame.go). Letterboxing loses
 * nothing, and cropping somebody's picture uninvited is the choice worth making
 * arguable rather than the one worth making silently.
 *
 * The colour of the bars is not here. ffmpeg pads the frame, not the picture
 * inside it, so the note paints the stage this sits on: stageStyle.
 */
export function frameStyle(segmentNotes: readonly RenderNote[]): React.CSSProperties {
  return { objectFit: noteValue("fit", [], segmentNotes) === "cover" ? "cover" : "contain" };
}

/** The shape of a box, which is its share of the frame at the frame's ratio. */
export function boxAspect(rect: Rect): number {
  return (rect.w / rect.h) * frameAspect;
}

/**
 * Whether this browser can act on the value written for a key it reads.
 *
 * The same question the renderer asks, and it has to be, because it is what
 * decides whether a note counts as having been said at all. A key nothing here
 * reads never reaches this, and a new one whose reader forgot to say what it
 * takes answers no rather than claiming a picture it did not draw.
 */
function actionable(key: string, value: string): boolean {
  switch (key) {
    case "fit":
      return value === "cover" || value === "contain";
    case "background":
      return parsableColour(value);
    case "whenShort":
      return value === "black" || value === "hold";
  }
  return false;
}

/**
 * The value of a render note, or undefined.
 *
 * A box's own note wins over the segment's, because "contain this one, cover
 * the other" is the ordinary case (ADR 000068).
 *
 * A value neither of them could act on drops out of that order rather than
 * ending it, which is the renderer's rule and used to not be this one. A box
 * asking for `fit: stretch` is a box that said nothing, so the segment's answer
 * stands; stopping at it instead reached past the segment to the layout's own
 * fit and drew a third picture neither of them asked for (issue #226).
 */
export function noteValue(
  key: string,
  boxNotes: readonly RenderNote[],
  segmentNotes: readonly RenderNote[] = [],
): string | undefined {
  for (const notes of [boxNotes, segmentNotes]) {
    // The first note naming the key, so a key written twice on one box resolves
    // the same way in both renderers.
    const value = notes.find((note) => note.key === key)?.value;
    if (value !== undefined && actionable(key, value)) return value;
  }
  return undefined;
}

/**
 * Where a leaf's content lands inside its box, as a fraction of the box.
 *
 * The crop comes first and the fit second, which is the order ffmpeg works in:
 * it cuts the source down and then pads or crops the result into the box. So
 * what is being fitted is the cropped region, and what it is fitted against is
 * the box.
 *
 * Leaning on object-fit instead put those two the wrong way round. It fits
 * against the element it is set on, and a cropped box's element is the whole
 * source scaled up, not the box, so the bars ended up centred in something
 * larger than the frame anybody was looking at (issue #217).
 *
 * A covering rect runs outside 0..1 on one axis, which is what covering means:
 * the overflow is clipped by the box.
 *
 * box and source are shapes, width over height.
 */
export function contentRect(
  crop: Rect | undefined,
  fit: LayoutFit,
  box: number,
  source: number,
): Rect {
  const cropped = source * ((crop?.w ?? 1) / (crop?.h ?? 1));
  // How much wider the cropped region is than its box, in box widths. Above one
  // it is the wider of the two, so containing it bars the top and bottom and
  // covering spills off the sides.
  const ratio = cropped / box;

  // Containing takes the smaller of the two answers on each axis and covering
  // takes the larger, which is the whole of the difference between them.
  const pick = fit === "contain" ? Math.min : Math.max;
  const w = pick(1, ratio);
  const h = pick(1, 1 / ratio);
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

/**
 * The CSS that fits content into its box, including a crop.
 *
 * The element carries the whole source and is sized so that the part the crop
 * names lands exactly where contentRect says. Its own shape is the source's, so
 * `fill` stretches nothing: the aspect ratio is already right and every fit
 * decision has been made in the arithmetic above rather than by the browser.
 *
 * The rest of the source is clipped away rather than left to the box, because
 * with a crop the content no longer covers its box: under contain there are
 * bars, and without the clip they would show the frames either side of the crop
 * instead of the background.
 *
 * sourceAspect is the source's own shape, which CSS cannot ask for. Until it is
 * known there is no way to place a crop, so nothing is drawn. That costs no
 * picture: an image or a video has nothing to show until it has read at least
 * as far as its own dimensions, which is the moment this stops being unknown.
 */
export function contentStyle(
  leaf: PlacedLeaf,
  segmentNotes: readonly RenderNote[] = [],
  sourceAspect?: number,
): React.CSSProperties {
  if (!sourceAspect) {
    return { position: "absolute", inset: 0, visibility: "hidden", display: "block" };
  }

  // The same precedence ffmpeg applies, because a preview that fits content
  // differently from the render is a preview that lies (ADR 000066).
  // Already known to be a fit this can draw, because a value that is not one
  // never comes back from noteValue.
  const fit: LayoutFit =
    (noteValue("fit", leaf.notes, segmentNotes) ?? leaf.fit) === "contain" ? "contain" : "cover";
  const crop = leaf.content.crop;

  const placed = contentRect(crop, fit, boxAspect(leaf.rect), sourceAspect);
  const shown = crop ?? wholeFrame;
  // The whole source, scaled so that the part of it being shown is the rect
  // above, and shifted so that part starts where the rect starts.
  const width = placed.w / shown.w;
  const height = placed.h / shown.h;

  return {
    position: "absolute",
    left: `${(placed.x - shown.x * width) * 100}%`,
    top: `${(placed.y - shown.y * height) * 100}%`,
    width: `${width * 100}%`,
    height: `${height * 100}%`,
    objectFit: "fill",
    display: "block",
    ...(crop
      ? {
          clipPath: `inset(${crop.y * 100}% ${(1 - crop.x - crop.w) * 100}% ${
            (1 - crop.y - crop.h) * 100
          }% ${crop.x * 100}%)`,
        }
      : {}),
  };
}
